import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';

import EntityHeaderPrimaryCta from './EntityHeaderPrimaryCta.jsx';
import { useConciergeHandoff } from '../hooks/useConciergeHandoff.js';

/**
 * The profile's Ask (CT-5721): "Ask about <name>", handed to the shell's
 * Concierge through `useConciergeHandoff`.
 *
 * It is its own component on purpose: the hand-off follows the shell's chat
 * actions, which change with every message the chat streams. Here only this
 * button re-renders with them; the profile page, its tabs and the open tab do
 * not.
 *
 * @param {object} props
 * @param {string} props.entityType
 * @param {string} props.entityId
 * @param {string} props.title — the name the header shows ("Untitled" included).
 */
const ProfileAskButton = memo(function ProfileAskButton({ entityType, entityId, title }) {
  const { t } = useTranslation('unified-profiles');
  const { openConcierge } = useConciergeHandoff();

  const handleAsk = useCallback(
    () => openConcierge({ intent: 'ask', entity: { type: entityType, id: entityId, title } }),
    [openConcierge, entityType, entityId, title],
  );

  const cta = useMemo(
    () => ({
      label: t('profile.ask.label'),
      ariaLabel: t('profile.ask.ariaLabel', { name: title }),
      tooltip: t('profile.ask.tooltip', { name: title }),
      onClick: handleAsk,
    }),
    [t, title, handleAsk],
  );

  return <EntityHeaderPrimaryCta cta={cta} />;
});

export default ProfileAskButton;
